// File: src/components/pages/tracker/TrackerTemplates.jsx
// Create and manage tracker templates.
// Each template has one Heading and multiple Item/Type rows.

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Plus, Trash2, Edit, LayoutTemplate,
  AlertCircle, CheckCircle, PlusCircle
} from 'lucide-react';
import { trackerTemplateAPI } from '../../../services/api';
import '../../../styles/unified.css';

const TYPE_OPTIONS = [
  { value: '',          label: 'Select type...' },
  { value: 'Scale',  label: 'Scale'       },
  { value: 'Checkbox', label: 'Checkbox'    },
  { value: 'Radio',    label: 'Radio'     },
  { value: 'Text',      label: 'Text'      },
];

// Colour per type — used in the table view badges
const TYPE_COLOURS = {
  'Scale':  '#667eea',
  'Checkbox': '#f59e0b',
  'Radio':    '#10b981',
  'Text':      '#6b7280',
};


const EMPTY_ROW  = () => ({ item: '', type: '' });
const EMPTY_FORM = () => ({ heading: '', rows: [EMPTY_ROW()] });

const TrackerTemplates = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [templates, setTemplates]   = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [isSaving, setIsSaving]     = useState(false);
  const [error, setError]           = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM());
  const [formErrors, setFormErrors] = useState({});

  // ── Load ─────────────────────────────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await trackerTemplateAPI.getTemplates(getAccessTokenSilently);
      if (response.success) setTemplates(response.data || []);
      else setError(response.message || 'Failed to load templates');
    } catch (err) {
      setError('Failed to load templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ── Form: heading ─────────────────────────────────────────────────────────────
  const handleHeadingChange = (e) => {
    setForm(prev => ({ ...prev, heading: e.target.value }));
    if (formErrors.heading) setFormErrors(prev => ({ ...prev, heading: '' }));
  };

  // ── Form: rows ────────────────────────────────────────────────────────────────
  const handleRowChange = (index, field, value) => {
    setForm(prev => {
      const rows = prev.rows.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      return { ...prev, rows };
    });
    // Clear the specific row error
    const key = `row_${index}_${field}`;
    if (formErrors[key]) setFormErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleAddRow = () => {
    setForm(prev => ({ ...prev, rows: [...prev.rows, EMPTY_ROW()] }));
  };

  const handleRemoveRow = (index) => {
    setForm(prev => ({
      ...prev,
      rows: prev.rows.filter((_, i) => i !== index)
    }));
    // Clear errors for that row
    setFormErrors(prev => {
      const next = { ...prev };
      delete next[`row_${index}_item`];
      delete next[`row_${index}_type`];
      return next;
    });
  };

  // ── Validation ────────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.heading.trim()) errs.heading = 'Heading is required';
    form.rows.forEach((row, i) => {
      if (!row.item.trim()) errs[`row_${i}_item`] = 'Item is required';
      if (!row.type)        errs[`row_${i}_type`] = 'Type is required';
    });
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Open / close ──────────────────────────────────────────────────────────────
  const handleNew = () => {
    setForm(EMPTY_FORM());
    setFormErrors({});
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (tpl) => {
    setForm({
      heading: tpl.heading,
      rows: (tpl.items || []).map(i => ({ item: i.item, type: i.type }))
    });
    setFormErrors({});
    setEditingId(tpl.templateId);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM());
    setFormErrors({});
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setError(null);

    const payload = {
      heading: form.heading.trim(),
      items:   form.rows.map(r => ({ item: r.item.trim(), type: r.type }))
    };

    try {
      const response = editingId
        ? await trackerTemplateAPI.updateTemplate(editingId, payload, getAccessTokenSilently)
        : await trackerTemplateAPI.createTemplate(payload, getAccessTokenSilently);

      if (response.success) {
        showSuccess(editingId ? 'Template updated' : 'Template created');
        handleCancel();
        await loadTemplates();
      } else {
        setError(response.message || 'Failed to save template');
      }
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    setError(null);
    try {
      const response = await trackerTemplateAPI.deleteTemplate(templateId, getAccessTokenSilently);
      if (response.success) { showSuccess('Template deleted'); await loadTemplates(); }
      else setError(response.message || 'Failed to delete template');
    } catch (err) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const formatDate = (d) => !d ? 'N/A' : new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Feedback */}
      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>
          <AlertCircle size={18} /><span>{error}</span>
        </div>
      )}
      {successMsg && (
        <div className="success-banner">
          <CheckCircle size={18} /><span>{successMsg}</span>
        </div>
      )}

      {/* Top bar */}
      <div className="search-bar-with-action" style={{ marginBottom: '1.25rem' }}>
        <div style={{ flex: 1 }} />
        {!showForm && (
          <button className="btn-new" onClick={handleNew}>
            <Plus size={20} />
            New Template
          </button>
        )}
      </div>

      {/* ── Form ── */}
      {showForm && (
        <div className="tracker-form-panel">
          <h3 className="tracker-form-title">
            {editingId ? 'Edit Template' : 'New Template'}
          </h3>

          {/* Heading */}
          <div className="tracker-form-field" style={{ marginBottom: '1.5rem' }}>
            <label className="tracker-form-label">Heading:</label>
            <input
              type="text"
              value={form.heading}
              onChange={handleHeadingChange}
              placeholder="e.g. Junctions"
              className={`tracker-form-input ${formErrors.heading ? 'input-error' : ''}`}
            />
            {formErrors.heading && <span className="field-error">{formErrors.heading}</span>}
          </div>

          {/* Column headers */}
          <div className="tracker-items-header">
            <span className="tracker-items-col-label">Item</span>
            <span className="tracker-items-col-label">Type</span>
          </div>

          {/* Item rows */}
          <div className="tracker-items-list">
            {form.rows.map((row, index) => (
              <div key={index} className="tracker-item-row">
                {/* Item input */}
                <div className="tracker-item-field">
                  <input
                    type="text"
                    value={row.item}
                    onChange={e => handleRowChange(index, 'item', e.target.value)}
                    placeholder="e.g. Approach Speed"
                    className={`tracker-form-input ${formErrors[`row_${index}_item`] ? 'input-error' : ''}`}
                  />
                  {formErrors[`row_${index}_item`] && (
                    <span className="field-error">{formErrors[`row_${index}_item`]}</span>
                  )}
                </div>

                {/* Type dropdown */}
                <div className="tracker-item-field">
                  <select
                    value={row.type}
                    onChange={e => handleRowChange(index, 'type', e.target.value)}
                    className={`tracker-form-input tracker-form-select ${formErrors[`row_${index}_type`] ? 'input-error' : ''}`}
                  >
                    {TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {formErrors[`row_${index}_type`] && (
                    <span className="field-error">{formErrors[`row_${index}_type`]}</span>
                  )}
                </div>

                {/* Remove row */}
                <button
                  className="tracker-row-remove"
                  onClick={() => handleRemoveRow(index)}
                  disabled={form.rows.length === 1}
                  title="Remove row"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Add row button */}
          <button className="tracker-add-row-btn" onClick={handleAddRow}>
            <PlusCircle size={17} />
            Add
          </button>

          {/* Form actions */}
          <div className="tracker-form-actions">
            <button className="btn-secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </button>
            <button
              className="btn-new"
              onClick={handleSubmit}
              disabled={isSaving}
              style={{ opacity: isSaving ? 0.65 : 1 }}
            >
              {isSaving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* ── List ── */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading templates...</p>
        </div>
      ) : templates.length === 0 && !showForm ? (
        <div className="empty-state">
          <LayoutTemplate size={48} style={{ color: '#94a3b8', marginBottom: '1rem' }} />
          <h3>No templates yet</h3>
          <p>Click <strong>+ New Template</strong> to add your first tracker template.</p>
        </div>
      ) : templates.length > 0 && (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Heading</th>
                <th>Items</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl) => (
                <tr key={tpl.templateId}>
                  <td><strong>{tpl.heading}</strong></td>
                  <td>
                    <div className="tracker-item-rows">
                      {(tpl.items || []).map((itm, i) => (
                        <div key={i} className="tracker-item-line">
                          <span className="badge-item-name">{itm.item}</span>
                          <span
                            className="badge-type-pill"
                            style={{ backgroundColor: TYPE_COLOURS[itm.type] || '#94a3b8' }}
                          >
                            {itm.type}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>{formatDate(tpl.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit" title="Edit" onClick={() => handleEdit(tpl)}>
                        <Edit size={16} />
                      </button>
                      <button className="action-btn delete" title="Delete" onClick={() => handleDelete(tpl.templateId)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="user-count" style={{ marginTop: '0.75rem' }}>
            {templates.length} template{templates.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </>
  );
};

export default TrackerTemplates;